import { Input, FormControl, FormErrorMessage, Text } from '@chakra-ui/core';
import React from 'react';
import theme from '../../../../../styles/customTheme';
import { isFieldSchema, Sections, SectionSchema } from '../formBuilderSchema';
import ErrorWrapper from '../../errorHandling/ErrorWrapper';
import useErrorHandling from '../../useErrorHandling';
import { useTranslation } from 'react-i18next';
import { useHireFormContext } from '../../HireForm.context';

interface SectionNameInputProps {
  name: string;
  sectionIndex: number;
  isAdvancedSection: boolean;
  setFormSchema: (value: SectionSchema[]) => void;
  path: (string | number)[];
}

const NameInput = React.memo(({ name, sectionIndex, setFormSchema, path }: Omit<SectionNameInputProps, 'isAdvancedSection'>) => {
  const { t } = useTranslation('hrFormTemplate');
  const [sectionName, setSectionName] = React.useState(name);
  const [isSectionNameAlreadyAvailable, setIsSectionNameAlreadyAvailable] = React.useState(false);
  const { formSchema } = useHireFormContext();

  React.useEffect(() => {
    setSectionName(name);
  }, [name]);

  const { validateError, error, hasError, inputRef } = useErrorHandling({
    path,
    valdationFor: 'section_name',
    onValidation: (value) => value,
  });

  function handleSectionNameChange(newSectionName: string) {
    setSectionName(newSectionName);
    const isDuplicateSectionName = formSchema?.some(
      (section, index) => section.section === newSectionName && index !== sectionIndex,
    );
    setIsSectionNameAlreadyAvailable(isDuplicateSectionName);
  }

  function handleSectionNameBlur() {
    validateError(sectionName, (value) => {
      if (!isSectionNameAlreadyAvailable) {
        const newFieldSchems = [...formSchema]?.map((section, index) => {
          if (sectionIndex === index) {
            return {
              ...section,
              fields: section.fields?.map((field) => {
                if (isFieldSchema(field)) {
                  return { ...field, section: value as string };
                } else {
                  const subFields = field.fields?.map((subField) => {
                    return { ...subField, parentSection: value as string };
                  });
                  return { ...field, fields: subFields };
                }
              }),
              section: sectionName,
            };
          }
          return section;
        });
        setFormSchema(newFieldSchems);
      }

      // setFormSchema((prev) => {
      //   if (isSectionNameAlreadyAvailable) return prev;

      //   return [...prev]?.map((section, index) => {
      //     if (sectionIndex === index) {
      //       return {
      //         ...section,
      //         fields: section.fields?.map((field) => {
      //           if (isFieldSchema(field)) {
      //             return { ...field, section: value as string };
      //           } else {
      //             const subFields = field.fields?.map((subField) => {
      //               return { ...subField, parentSection: value as string };
      //             });
      //             return { ...field, fields: subFields };
      //           }
      //         }),
      //         section: sectionName,
      //       };
      //     }
      //     return section;
      //   });
      // });
    });
  }

  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (inputRef.current && !inputRef.current.contains(event.target as Node)) {
        inputRef.current?.blur();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <FormControl isInvalid={isSectionNameAlreadyAvailable}>
      <ErrorWrapper ref={inputRef} error={t(error?.message!) || ''} hasError={isSectionNameAlreadyAvailable || hasError}>
        <Input
          variant="unstyled"
          size="sm"
          fontWeight="bold"
          borderRadius={theme.radii.md}
          paddingX={1}
          paddingY={0}
          placeholder={t('formBuilder.sectionInputPlaceholder')}
          color={isSectionNameAlreadyAvailable ? theme.colors.red[500] : '#4A5568'}
          value={sectionName}
          onChange={(e) => {
            e.preventDefault();
            handleSectionNameChange(e.target.value);
          }}
          onBlur={handleSectionNameBlur}
          width="100%"
          isTruncated
          ref={inputRef}
          _focus={{
            fontWeight: 'normal',
          }}
        />
      </ErrorWrapper>
      <FormErrorMessage marginTop={0} fontSize="xs">
        {t('formBuilder.sectionNameExistsMessage')}
      </FormErrorMessage>
    </FormControl>
  );
});

export const formatLabel = (field: string): string => {
  const sectionToCaps = [Sections.sin, Sections.ssn];
  if (field && sectionToCaps.includes(field as Sections)) {
    return field.toUpperCase();
  }
  // If all characters are uppercase, return the field as is
  if (field === field.toUpperCase()) {
    return field;
  }

  const words = field?.match(/[A-Z]?[a-z]+|[0-9]+/g);
  return words?.map((word, index) => (index === 0 ? word.charAt(0).toUpperCase() + word.slice(1) : word)).join(' ') ?? '';
};

const SectionNameInput = React.memo((props: SectionNameInputProps) => {
  if (props.isAdvancedSection)
    return (
      <Text size="sm" fontWeight="bold" opacity={0.8} cursor="not-allowed">
        {formatLabel(props.name)}
      </Text>
    );
  return <NameInput {...props} />;
});

export default SectionNameInput;
